import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { ShopperReservationMode } from '@iheartjane/types';

import { isCurbsideScheduleOpen } from '@jane/shared-ecomm/util';
import type { StoreWithSchedules } from '@jane/shared-ecomm/util';

interface StoreForShopperReservationModes {
  curbside_pickup_setting?: 'allowed' | 'not_allowed' | 'mandatory' | null;
  delivery?: boolean | null;
  pickup?: boolean | null;
}

export const useAvailableShopperReservationModes = (
  store: StoreForShopperReservationModes & StoreWithSchedules,
  // When provided, hides modes whose schedule is always-closed. Omit for a
  // capability-only filter (e.g. CheckoutToggle).
  scheduleOpenModes?: ReadonlyArray<ShopperReservationMode>,
  // Capability-mode "curbside accepting now" for the mandatory pickup fallback;
  // falls back to the weekly schedule when omitted.
  curbsideAcceptingNow?: boolean
): ShopperReservationMode[] => {
  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;

  const hasDelivery = Boolean(store?.delivery);
  const hasPickupCapability = Boolean(store?.pickup);
  const curbsideSetting = store?.curbside_pickup_setting;
  const offersCurbside =
    curbsideFlagOn &&
    hasPickupCapability &&
    (curbsideSetting === 'allowed' || curbsideSetting === 'mandatory');

  const deliveryScheduleOpen =
    !scheduleOpenModes || scheduleOpenModes.includes('delivery');
  const pickupScheduleOpen =
    !scheduleOpenModes || scheduleOpenModes.includes('pickup');
  const curbsideScheduleOpen =
    !scheduleOpenModes || scheduleOpenModes.includes('curbside');

  const curbsideOpen = offersCurbside
    ? (curbsideAcceptingNow ??
      (scheduleOpenModes
        ? scheduleOpenModes.includes('curbside')
        : isCurbsideScheduleOpen(store)))
    : false;

  const curbsideAvailable = offersCurbside && curbsideScheduleOpen;
  const pickupAvailable =
    pickupScheduleOpen &&
    hasPickupCapability &&
    !(curbsideSetting === 'mandatory' && curbsideOpen);

  const modes: ShopperReservationMode[] = [];
  if (hasDelivery && deliveryScheduleOpen) modes.push('delivery');
  if (pickupAvailable) modes.push('pickup');
  if (curbsideAvailable) modes.push('curbside');

  return modes;
};
